import { useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, RefreshCw, ImagePlus, Pencil, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, mediaUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/site";

export function VideoRow({ video, index, folders = [], canDrag = true, onChanged, onDeleted, onDragStart, onDragOver, onDrop }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState({ title: video.title, brand: video.brand, categories: video.categories?.length ? video.categories : [video.category].filter(Boolean), description: video.description || "", format: video.format, folder_id: video.folder_id || "" });
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);
  const coverInput = useRef(null);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/videos/${video.id}`, form);
      toast.success("Video updated.");
      setEditOpen(false);
      onChanged(data);
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (checked) => {
    try {
      const { data } = await api.put(`/admin/videos/${video.id}`, { visible: checked });
      onChanged(data);
    } catch {
      toast.error("Could not update visibility.");
    }
  };

  const replaceFile = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.put(`/admin/videos/${video.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video file replaced — tags and position kept.");
      onChanged(data);
    } catch {
      toast.error("Replace failed.");
    }
  };

  const replaceCover = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("cover", f);
    try {
      const { data } = await api.put(`/admin/videos/${video.id}/thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover image updated.");
      onChanged(data);
    } catch {
      toast.error("Cover upload failed.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/videos/${video.id}`);
      toast.success("Video removed.");
      onDeleted(video.id);
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={onDrop}
      data-testid={`admin-video-row-${video.id}`}
      className="flex flex-wrap items-center gap-3 md:gap-4 bg-white border border-cream-dark p-3 md:p-4"
    >
      <span className="cursor-grab text-ink-soft/60 hover:text-ink" data-testid={`drag-handle-${video.id}`} aria-label="Drag to reorder">
        <GripVertical className="w-5 h-5" />
      </span>
      <div className={`bg-cream-dark overflow-hidden shrink-0 ${video.format === "vertical" ? "w-12 aspect-[9/16]" : "w-24 aspect-video"}`}>
        {video.thumbnail_url && <img src={mediaUrl(video.thumbnail_url)} alt={video.title} className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink text-sm truncate" data-testid={`video-title-${video.id}`}>{video.title}</p>
        <p className="text-xs text-ink-soft mt-0.5 truncate">
          {(video.categories?.length ? video.categories : [video.category]).filter(Boolean).join(" · ")} · {video.format === "vertical" ? "9:16" : "16:9"}{video.brand ? ` · ${video.brand}` : ""}
          {folders.find((f) => f.id === video.folder_id) && (
            <span className="ml-1 text-ink font-medium">· {folders.find((f) => f.id === video.folder_id).name}</span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <button data-testid={`replace-file-${video.id}`} onClick={() => fileInput.current?.click()} title="Replace video file"
          className="w-9 h-9 flex items-center justify-center border border-cream-deeper text-ink-soft hover:border-ink hover:text-ink transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
        <button data-testid={`replace-cover-${video.id}`} onClick={() => coverInput.current?.click()} title="Upload custom cover"
          className="w-9 h-9 flex items-center justify-center border border-cream-deeper text-ink-soft hover:border-ink hover:text-ink transition-colors">
          <ImagePlus className="w-4 h-4" />
        </button>
        <button data-testid={`edit-video-${video.id}`} onClick={() => { setForm({ title: video.title, brand: video.brand, categories: video.categories?.length ? video.categories : [video.category].filter(Boolean), description: video.description || "", format: video.format, folder_id: video.folder_id || "" }); setEditOpen(true); }} title="Edit metadata"
          className="w-9 h-9 flex items-center justify-center border border-cream-deeper text-ink-soft hover:border-ink hover:text-ink transition-colors">
          <Pencil className="w-4 h-4" />
        </button>
        <button data-testid={`delete-video-${video.id}`} onClick={() => setDeleteOpen(true)} title="Delete video"
          className="w-9 h-9 flex items-center justify-center border border-cream-deeper text-ink-soft hover:border-ink hover:text-ink transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.14em] text-ink-soft">{video.visible ? "Live" : "Hidden"}</span>
        <Switch checked={video.visible} onCheckedChange={toggleVisible} data-testid={`visibility-toggle-${video.id}`} aria-label="Toggle visibility" />
      </div>
      <input ref={fileInput} type="file" accept=".mp4,.mov" className="hidden" data-testid={`replace-file-input-${video.id}`} onChange={(e) => replaceFile(e.target.files?.[0])} />
      <input ref={coverInput} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid={`replace-cover-input-${video.id}`} onChange={(e) => replaceCover(e.target.files?.[0])} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white rounded-none" data-testid="edit-video-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit video</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="eyebrow text-ink-soft">Title</Label>
              <Input data-testid="edit-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-2 rounded-none" />
            </div>
            <div>
              <Label className="eyebrow text-ink-soft">Brand</Label>
              <Input data-testid="edit-brand-input" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} className="mt-2 rounded-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="eyebrow text-ink-soft">Categories <span className="normal-case tracking-normal font-normal text-ink-soft/60">(multi)</span></Label>
                <div className="mt-2 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto" data-testid="edit-category-picker">
                  {CATEGORIES.map((c) => (
                    <button
                      type="button"
                      key={c}
                      data-testid={`edit-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }))}
                      className={`text-[10px] uppercase tracking-[0.1em] font-semibold px-2.5 py-1.5 rounded-full border transition-colors ${
                        form.categories.includes(c) ? "bg-ink text-cream border-ink" : "border-cream-deeper text-ink-soft hover:border-ink"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="eyebrow text-ink-soft">Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm((f) => ({ ...f, format: v }))}>
                  <SelectTrigger data-testid="edit-format-select" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vertical">Vertical 9:16</SelectItem>
                    <SelectItem value="horizontal">Horizontal 16:9</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="eyebrow text-ink-soft">Description <span className="normal-case tracking-normal font-normal text-ink-soft/60">(shown under the card)</span></Label>
              <Input data-testid="edit-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-2 rounded-none" placeholder="One line about this clip" />
            </div>
            <div>
              <Label className="eyebrow text-ink-soft">Folder</Label>
              <Select value={form.folder_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, folder_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="edit-folder-select" className="mt-2 rounded-none"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="edit-folder-none">Unfiled</SelectItem>
                  {folders.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`edit-folder-${f.id}`}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button onClick={saveEdit} disabled={saving} data-testid="edit-save-button"
              className="w-full bg-ink hover:bg-ink disabled:opacity-60 text-cream py-3 text-sm font-semibold transition-colors">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white rounded-none" data-testid="delete-video-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this video?</AlertDialogTitle>
            <AlertDialogDescription>
              “{video.title}” will be permanently removed from the live video wall. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-none">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={doDelete} className="rounded-none bg-ink hover:bg-neutral-800 text-cream">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
